import { setMapLayerLayoutProperty, getMapBoxResourceId } from "utils/layers/mapUtils";
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCombinedLayers } from 'redux/selectors/layersSelectors';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import Grid from "@mui/material/Grid";
import SeeItem from "./SeeItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { updateLayersOrderAction } from "redux/slices/layers/rasterLayersSlice";

// Build transform with rounded pixel values to prevent blur during drag
// (sub-pixel transform + transition causes blurred text/graphics)
const getCrispTransform = (transform) => {
  if (!transform) return undefined;
  const x = Math.round(transform.x ?? 0);
  const y = Math.round(transform.y ?? 0);
  // Important: ignore scaleX/scaleY from dnd-kit to prevent cards
  // (like Cut-Fill with legend) from rendering "squeezed".
  return `translate3d(${x}px, ${y}px, 0)`;
};

const SortableItem = ({ layer, onToggleWmsLayer }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: layer.name });

  const style = {
    transform: getCrispTransform(transform),
    // Disable transition while dragging to avoid blur from interpolated sub-pixel values
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.92 : 1,
    width: '100%',
  };

  return (
    <Grid item xs={12}>
      <div ref={setNodeRef} style={style} {...attributes}>
        <SeeItem
          layerData={layer.data}
          layerKey={layer.name}
          provided={{ dragHandleProps: listeners }}
          onToggleWmsLayer={onToggleWmsLayer}
        />
      </div>
    </Grid>
  );
};

const SeeList = () => {
  const dispatch = useDispatch();
  const combinedLayers = useSelector(selectCombinedLayers); 
  const [wmsVisibility, setWmsVisibility] = useState(true);
  const selectFlightId = useSelector(state => state.flightSelected?.id);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !window.map) {
      return;
    }

    const oldIndex = combinedLayers.findIndex((layer) => layer.name === active.id);
    const newIndex = combinedLayers.findIndex((layer) => layer.name === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Reorder the layers array
    const reorderedLayers = arrayMove(combinedLayers, oldIndex, newIndex);

    // Update order property for each layer
    const updatedLayers = reorderedLayers.map((layer, index) => ({
      ...layer,
      data: {
        ...layer.data,
        order: index,
      },
    }));

    // Update Redux state
    dispatch(updateLayersOrderAction(updatedLayers));

    // Update map layer positions
    updateMapLayerPositions(updatedLayers);
  };

  const updateMapLayerPositions = (layers) => {
    if (!window.map) return;

    try {
      // Get all layer IDs in the new order (array index 0 = bottom, last = top)
      const layerIds = [];
      
      layers.forEach((layer) => {
        const layerType = layer?.data?.type;

        if (layerType === 'rasterLayer') {
          const layerId = getMapBoxResourceId(layer.data.name, selectFlightId, true);
          if (window.map.getLayer(layerId)) {
            layerIds.push(layerId);
          }
        } else if (layerType === 'wmsLayer') {
          // WMS layers - check each one and add if exists
          const wmsLayerIds = ['parcel-fabric-wms-layer', 'admin-boundaries-wms-layer', 'storm_lines'];
          wmsLayerIds.forEach(layerId => {
            if (window.map.getLayer(layerId) && !layerIds.includes(layerId)) {
              layerIds.push(layerId);
            }
          });
        } else if (layerType === 'linework') {
          const { uuid } = layer.data;
          const lineLayerId = `linework-line-${uuid}`;
          const pointLayerId = `linework-point-${uuid}`;
          const polygonLayerId = `linework-polygon-${uuid}`;

          // Add linework layers in the correct order (line, point, polygon)
          [lineLayerId, pointLayerId, polygonLayerId].forEach(layerId => {
            if (window.map.getLayer(layerId)) {
              layerIds.push(layerId);
            }
          });
        }
      });

      for (let i = layerIds.length - 1; i >= 0; i--) {
        const currentLayerId = layerIds[i];
        
        try {
          window.map.moveLayer(currentLayerId);
        } catch (error) {
          // Layer might not exist or already in position, continue
          console.warn(`Could not move layer ${currentLayerId}:`, error);
        }
      }
    } catch (error) {
      console.error('Error updating map layer positions:', error);
    }
  };

  const toggledWmsLayer = () => {
    setWmsVisibility(!wmsVisibility);
    setMapLayerLayoutProperty('storm_lines', 'visibility', !wmsVisibility ? 'visible' : 'none');
    setMapLayerLayoutProperty('admin-boundaries-wms-layer', 'visibility', !wmsVisibility ? 'visible' : 'none');
    setMapLayerLayoutProperty('parcel-fabric-wms-layer', 'visibility', !wmsVisibility ? 'visible' : 'none');
  };

  return (
    <>
      <Box alignItems="center" display="flex" flexDirection="column" pt={2} pb={2}>
        <Typography variant="h4">See</Typography>
      </Box>
      <Divider />
      {combinedLayers?.length > 0 && window.map ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={combinedLayers.map(layer => layer.name)}
            strategy={verticalListSortingStrategy}
          >
            <Grid
              container
              spacing={1}
              sx={{ padding: "8px 8px 8px", width: '100%' }}
            >
              {combinedLayers.map((layer) => (
                <SortableItem
                  key={layer.name}
                  layer={layer}
                  onToggleWmsLayer={toggledWmsLayer}
                />
              ))}
            </Grid>
          </SortableContext>
        </DndContext>
      ) : (
        <Grid
          container
          spacing={1}
          sx={{ padding: "8px 8px 8px" }}
        >
          {combinedLayers?.map((layer) => (
            <Grid item container key={layer.name}>
              <Grid item xs>
                <SeeItem
                  layerData={layer.data}
                  layerKey={layer.name}
                  provided={{ dragHandleProps: {} }}
                  onToggleWmsLayer={toggledWmsLayer}
                />
              </Grid>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );
};

export default SeeList;